import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { FileText, Download, CheckCircle2, Loader2, Anchor, Pen, Trash2, CreditCard, Ship, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignatureCanvas = ({ onSave, width = 500, height = 180 }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasDrawn(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0A2540';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }, [isDrawing, getPos]);

  const endDraw = useCallback(() => {
    setIsDrawing(false);
    if (hasDrawn && canvasRef.current) onSave(canvasRef.current.toDataURL('image/png'));
  }, [hasDrawn, onSave]);

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasDrawn(false);
    onSave(null);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const opts = { passive: false };
    canvas.addEventListener('touchstart', startDraw, opts);
    canvas.addEventListener('touchmove', draw, opts);
    canvas.addEventListener('touchend', endDraw);
    return () => {
      canvas.removeEventListener('touchstart', startDraw);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', endDraw);
    };
  }, [startDraw, draw, endDraw]);

  return (
    <div className="space-y-2">
      <canvas ref={canvasRef} width={width} height={height}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        className="border-2 border-dashed border-[#D4AF37] rounded-xl bg-white cursor-crosshair w-full"
        style={{ touchAction: 'none', maxWidth: `${width}px` }} />
      {hasDrawn && (
        <Button variant="outline" size="sm" className="text-xs" onClick={clear}><Trash2 className="h-3 w-3 mr-1" /> Effacer</Button>
      )}
    </div>
  );
};

const DevisSignPage = () => {
  const { signToken } = useParams();
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [signedBy, setSignedBy] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCGV, setShowCGV] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);

  useEffect(() => {
    const fetchDevis = async () => {
      try {
        const res = await axios.get(`${API}/devis/public/${signToken}`);
        setDevis(res.data);
        setSignedBy(res.data.client_name || '');
        if (res.data.status === 'signed') setSigned(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Devis introuvable ou lien invalide');
      } finally { setLoading(false); }
    };
    fetchDevis();
  }, [signToken]);

  const handleSign = async () => {
    if (!signatureData) { toast.error('Veuillez signer dans le cadre'); return; }
    if (!signedBy.trim()) { toast.error('Veuillez indiquer votre nom'); return; }
    if (!cgvAccepted) { toast.error('Vous devez accepter les Conditions Générales de Vente'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/devis/public/${signToken}/sign`, {
        signature_data: signatureData,
        signed_by: signedBy,
      });
      const successMsg = isOffre ? 'Offre acceptee ! Votre reservation a ete creee.' : 'Devis signe avec succes !';
      toast.success(successMsg);
      setSigned(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/devis/public/${signToken}/pdf`, { responseType: 'blob', timeout: 30000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Devis_SOGNUDIMARE.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error('Erreur telechargement - veuillez reessayer'); }
    finally { setDownloadingPdf(false); }
  };

  // Fix broken local upload paths or empty photos — use default photos
  const rawBoatPhotos = devis?.boat_photos || [];
  const rawBoatPlans = devis?.boat_plans || [];
  const [boatPhotos, setBoatPhotos] = useState([]);
  const [boatPlans, setBoatPlans] = useState([]);

  useEffect(() => {
    if (!devis) return;
    const hasBroken = rawBoatPhotos.length === 0 || rawBoatPhotos.some(p => p.startsWith('/api/uploads/'));
    if (hasBroken && devis.catamaran) {
      axios.get(`${API}/boat-default-photos/${encodeURIComponent(devis.catamaran)}`)
        .then(res => {
          if (res.data.photos?.length) setBoatPhotos(res.data.photos);
          else setBoatPhotos(rawBoatPhotos);
          if (res.data.plans?.length) setBoatPlans(res.data.plans);
          else setBoatPlans(rawBoatPlans);
        }).catch(() => { setBoatPhotos(rawBoatPhotos); setBoatPlans(rawBoatPlans); });
    } else {
      setBoatPhotos(rawBoatPhotos);
      setBoatPlans(rawBoatPlans);
    }
  }, [devis]);

  if (loading) return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" /></div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center p-4">
      <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
        <Anchor className="h-12 w-12 text-[#D4AF37] mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#0A2540] mb-2">Lien invalide</h2>
        <p className="text-[#4A5568]">{error}</p>
      </CardContent></Card>
    </div>
  );

  const isPrivat = devis.type === 'privatization';
  const isOffre = devis.category === 'offre';
  const docLabel = isOffre ? 'Offre' : 'Devis';
  const inclusions = devis.inclusions || [];
  const exclusions = devis.exclusions || [];

  const fmtDate = (d) => {
    if (!d) return '-';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return d;
  };

  return (
    <div className={`min-h-screen ${isOffre ? 'bg-[#1A1A2E]' : 'bg-[#0A2540]'}`} data-testid="devis-sign-page">
      {/* Header */}
      {isOffre ? (
        /* OFFRE: en-tete promotionnel terracotta/cuivre */
        <div className="pt-8 pb-6 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/logo_sognudimare.png`}
                alt="SOGNUDIMARE"
                className="h-10 sm:h-14 object-contain"
                style={{ aspectRatio: '2/1' }}
                onError={(e) => { e.target.style.display = 'none'; }}
                data-testid="devis-sign-logo"
              />
              <div className="bg-[#C4724F] text-white px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase">
                Offre speciale
              </div>
            </div>
            <div className="h-0.5 bg-gradient-to-r from-[#C4724F] via-[#D4AF37] to-transparent" />
          </div>
        </div>
      ) : (
        /* DEVIS: en-tete classique centré or */
        <div className="pt-8 pb-6 px-4 text-center">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/logo_sognudimare.png`}
            alt="SOGNUDIMARE"
            className="h-14 sm:h-18 mx-auto mb-3 object-contain"
            style={{ aspectRatio: '2/1' }}
            onError={(e) => { e.target.style.display = 'none'; }}
            data-testid="devis-sign-logo"
          />
          <p className="text-[#D4AF37] text-sm tracking-wider">Croisieres catamaran en Corse</p>
          <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto mt-4" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 pb-12">
        {/* Document Card */}
        <Card className="shadow-2xl border-0 overflow-hidden mb-6">
          {/* Top bar */}
          <div className={`h-1.5 ${isOffre 
            ? 'bg-gradient-to-r from-[#C4724F] via-[#E8956B] to-[#C4724F]' 
            : 'bg-gradient-to-r from-[#D4AF37] via-[#C4724F] to-[#D4AF37]'}`} />

          <CardContent className="p-5 sm:p-8">
            {/* Status + Number */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className={`text-xs uppercase tracking-wider font-medium ${isOffre ? 'text-[#C4724F]' : 'text-[#94A3B8]'}`}>{docLabel}</p>
                <h2 className="text-2xl font-bold text-[#0A2540]">{devis.devis_number}</h2>
                <p className={`text-sm font-semibold mt-0.5 ${isOffre ? 'text-[#C4724F]' : 'text-[#C4724F]'}`}>
                  {isPrivat ? 'Privatisation complete' : 'Reservation a la cabine'}
                </p>
              </div>
              {signed && (
                <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full border border-emerald-200">
                  <CheckCircle2 className="h-5 w-5" /><span className="font-bold text-sm">{isOffre ? 'ACCEPTEE' : 'SIGNE'}</span>
                </div>
              )}
            </div>

            {/* Intro text */}
            {devis.intro_text && (
              <div className="mb-6 bg-[#D4AF37]/5 border-l-4 border-[#D4AF37] rounded-r-lg px-4 py-3">
                <p className="text-sm text-[#4A5568] italic whitespace-pre-line">{devis.intro_text}</p>
              </div>
            )}

            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
              <div className="space-y-2.5 bg-[#F8F9FA] rounded-xl p-4">
                <p><span className="text-[#94A3B8] text-xs uppercase">Client</span><br/><span className="font-bold text-[#0A2540]">{devis.client_name}</span></p>
                <p><span className="text-[#94A3B8] text-xs uppercase">Programme</span><br/><span className="font-semibold text-[#0A2540]">{devis.program || devis.cruise_name}</span></p>
                <p><span className="text-[#94A3B8] text-xs uppercase">Passagers</span><br/><span className="font-semibold text-[#0A2540]">{devis.passenger_count}</span></p>
              </div>
              <div className="space-y-2.5 bg-[#F8F9FA] rounded-xl p-4">
                <p><span className="text-[#94A3B8] text-xs uppercase">Dates</span><br/><span className="font-bold text-[#0A2540]">{fmtDate(devis.start_date)} → {fmtDate(devis.end_date)}</span></p>
                <p><span className="text-[#94A3B8] text-xs uppercase">Duree</span><br/><span className="font-semibold text-[#0A2540]">{devis.duration}</span></p>
                <p><span className="text-[#94A3B8] text-xs uppercase">Catamaran</span><br/><span className="font-semibold text-[#0A2540]">{devis.catamaran}</span></p>
              </div>
            </div>

            {/* Boat Photos */}
            {boatPhotos.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium mb-3 flex items-center gap-1.5">
                  <Ship className="h-3.5 w-3.5" /> Le navire
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 rounded-xl overflow-hidden">
                  {boatPhotos.slice(0, 6).map((url, i) => (
                    <img key={i}
                      src={url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url}
                      alt={`Bateau ${i+1}`}
                      className={`w-full object-cover rounded-lg ${i === 0 ? 'sm:col-span-2 sm:row-span-2 h-40 sm:h-48' : 'h-28 sm:h-[92px]'}`} />
                  ))}
                </div>
              </div>
            )}

            {/* Boat Plans */}
            {boatPlans.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium mb-3">Plan du navire</h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  {boatPlans.map((url, i) => (
                    <img key={i}
                      src={url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url}
                      alt={`Plan ${i+1}`}
                      className="w-full sm:max-h-48 rounded-lg border object-contain" />
                  ))}
                </div>
              </div>
            )}

            {/* Pricing */}
            <div className="bg-[#0A2540] rounded-2xl p-4 sm:p-6 text-white mb-6">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1 mb-4">
                <span className="text-sm text-white/60 uppercase tracking-wider">Total TTC</span>
                <span className="text-2xl sm:text-4xl font-bold text-[#D4AF37]">{(devis.total || 0).toLocaleString('fr-FR')} EUR</span>
              </div>
              {devis.payment_schedule?.length > 0 && (
                <div className="pt-4 border-t border-white/15 space-y-2.5">
                  <p className="text-xs text-white/40 uppercase font-semibold tracking-wider">Echeancier de paiement</p>
                  {devis.payment_schedule.map((p, i) => (
                    <div key={i} className="flex justify-between text-xs sm:text-sm gap-2">
                      <span className="text-white/70 break-words">{p.label || p.condition}</span>
                      <span className="font-bold whitespace-nowrap">{(p.amount || 0).toLocaleString('fr-FR')} EUR</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Square Payment Link */}
            {devis.square_payment_link && (
              <div className="mb-6">
                {!signed && (
                  <label className="flex items-start gap-2.5 text-xs leading-relaxed cursor-pointer select-none p-3 mb-2.5 rounded-lg bg-[#FFF7ED] border border-[#FED7AA]" data-testid="pay-cgv-label">
                    <input
                      type="checkbox"
                      checked={cgvAccepted}
                      onChange={e => setCgvAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#C4724F] shrink-0 cursor-pointer"
                      data-testid="pay-cgv-checkbox"
                    />
                    <span className="text-[#4A5568]">
                      J'ai lu et j'accepte les{' '}
                      <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-[#C4724F] hover:text-[#9A3412] font-semibold underline">Conditions Générales de Vente</a>{' '}
                      de SOGNUDIMARE. <span className="text-[#C4724F] font-bold">*</span>
                    </span>
                  </label>
                )}
                {signed || cgvAccepted ? (
                  <a href={devis.square_payment_link} target="_blank" rel="noreferrer"
                    className="flex items-center justify-center gap-3 w-full py-4 rounded-xl bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white font-bold text-base transition-colors" data-testid="devis-pay-link">
                    <CreditCard className="h-5 w-5" /> Payer le 1er versement en ligne
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="flex items-center justify-center gap-3 w-full py-4 rounded-xl bg-gray-300 text-white font-bold text-base cursor-not-allowed"
                    data-testid="devis-pay-link-disabled"
                  >
                    <CreditCard className="h-5 w-5" /> Payer le 1er versement en ligne
                  </button>
                )}
              </div>
            )}

            {/* Expandable details */}
            <button onClick={() => setShowDetails(!showDetails)}
              className="flex items-center justify-between w-full py-3 px-4 rounded-xl bg-[#F8F9FA] hover:bg-[#F0F2F4] transition-colors text-sm font-medium text-[#0A2540] mb-4">
              <span>Detail de la prestation</span>
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {showDetails && (
              <div className="mb-6 space-y-4 px-1">
                {inclusions.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wider mb-2">Ce tarif comprend</h4>
                    <ul className="space-y-1.5">
                      {inclusions.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#4A5568]">
                          <CheckCircle2 className="h-4 w-4 text-[#2A9D8F] shrink-0 mt-0.5" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {exclusions.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-[#C4724F] uppercase tracking-wider mb-2">Le tarif ne comprend pas</h4>
                    <ul className="space-y-1.5">
                      {exclusions.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#4A5568]">
                          <span className="text-[#C4724F] shrink-0 font-bold">-</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Caution */}
                {devis.caution_per_person > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h4 className="text-xs font-bold text-amber-800 uppercase mb-2">Caution</h4>
                    <p className="text-sm text-amber-900 whitespace-pre-line">{devis.caution_text || `Caution : ${devis.caution_per_person} EUR / passager`}</p>
                  </div>
                )}
              </div>
            )}

            {/* Download PDF */}
            <Button variant="outline" className="w-full mb-6 h-12 text-[#0A2540] border-[#0A2540]/20" onClick={downloadPdf} disabled={downloadingPdf}>
              {downloadingPdf ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generation en cours...</> : <><Download className="h-4 w-4 mr-2" /> Telecharger {isOffre ? "l'offre complete" : "le devis complet"} (PDF avec CGV)</>}
            </Button>

            {/* CGV toggle */}
            <button onClick={() => setShowCGV(!showCGV)}
              className="flex items-center justify-between w-full py-3 px-4 rounded-xl bg-[#F8F9FA] hover:bg-[#F0F2F4] transition-colors text-sm font-medium text-[#0A2540] mb-4">
              <span>Conditions generales de vente</span>
              {showCGV ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {showCGV && (
              <div className="mb-6 bg-[#F8F9FA] rounded-xl p-4 max-h-96 overflow-y-auto text-xs text-[#4A5568] space-y-3">
                <p className="font-bold text-[#0A2540]">En signant ce devis, vous acceptez l'integralite des conditions generales de vente jointes au document PDF.</p>
                <p>Les CGV completes incluent : prestations, restauration, boissons, confort a bord, equipements, embarquement, fonctions equipage, itineraires, securite, annulation/modification, regles de vie, litiges, informations pratiques.</p>
                <p>Consultez le PDF pour le detail complet des conditions.</p>
              </div>
            )}

            {/* Signature */}
            {!signed ? (
              <div className="border-2 border-[#D4AF37] rounded-2xl p-5 space-y-4" data-testid="devis-sign-area">
                <h3 className="font-bold text-[#0A2540] flex items-center gap-2 text-lg">
                  <Pen className="h-5 w-5 text-[#D4AF37]" /> {isOffre ? "Accepter l'offre" : "Signer le devis"}
                </h3>
                <p className="text-sm text-[#4A5568]">
                  En signant {isOffre ? "cette offre" : "ce devis"}, vous acceptez les <b>conditions generales de vente</b> et les <b>regles de vie a bord</b>.
                  {isOffre && <span className="block mt-1 font-semibold text-[#C4724F]">Votre reservation sera automatiquement confirmee.</span>}
                </p>
                <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-xl p-4">
                  <p className="text-sm text-[#0A2540] font-semibold italic">"Lu et approuve, bon pour acceptation"</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#0A2540] block mb-1">Votre nom complet</label>
                  <Input value={signedBy} onChange={e => setSignedBy(e.target.value)} className="max-w-sm" placeholder="Nom et prenom" data-testid="devis-signed-by" />
                </div>

                <div>
                  <label className="text-sm font-medium text-[#0A2540] block mb-2">Votre signature</label>
                  <SignatureCanvas onSave={setSignatureData} width={500} height={180} />
                </div>

                {/* Mandatory CGV acceptance */}
                <label className="flex items-start gap-2.5 text-sm leading-relaxed cursor-pointer select-none p-3.5 rounded-xl bg-[#FFF7ED] border border-[#FED7AA] hover:border-[#FB923C] transition-colors" data-testid="devis-cgv-checkbox-label">
                  <input
                    type="checkbox"
                    checked={cgvAccepted}
                    onChange={e => setCgvAccepted(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-[#C4724F] shrink-0 cursor-pointer"
                    data-testid="devis-cgv-checkbox"
                  />
                  <span className="text-[#4A5568]">
                    J'ai lu et j'accepte les{' '}
                    <a
                      href="/cgv"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#C4724F] hover:text-[#9A3412] font-semibold underline underline-offset-2"
                    >
                      Conditions Générales de Vente
                    </a>
                    {' '}de SOGNUDIMARE. <span className="text-[#C4724F] font-bold">*</span>
                  </span>
                </label>

                <Button className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white text-lg py-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSign} disabled={submitting || !signatureData || !cgvAccepted} data-testid="devis-submit-sign">
                  {submitting ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Signature en cours...</> : <><CheckCircle2 className="h-5 w-5 mr-2" /> {isOffre ? "Accepter l'offre" : "Signer et accepter le devis"}</>}
                </Button>
              </div>
            ) : (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6 text-center" data-testid="devis-signed-confirmation">
                <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-emerald-800 mb-2">{isOffre ? 'Offre acceptee !' : 'Devis signe !'}</h3>
                <p className="text-emerald-700">
                  Merci {devis.signed_by || devis.client_name}.
                  {isOffre && <span className="block mt-1 font-semibold">Votre reservation a ete confirmee automatiquement.</span>}
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                  Signe le {devis.signed_at ? new Date(devis.signed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "aujourd'hui"}
                </p>
                <p className="text-sm text-emerald-600 mt-3">L'equipe SOGNUDIMARE vous contactera prochainement.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-white/40 space-y-1 pb-8">
          <p className="text-[#D4AF37] font-semibold tracking-wider mb-2">SOGNUDIMARE</p>
          <p>Route des Sanguinaires, 20000 AJACCIO</p>
          <p>contact@sognudimare-catamarans.com | 07 62 45 74 42</p>
          <p>sognudimare.com</p>
        </div>
      </div>
    </div>
  );
};

export default DevisSignPage;
